"use client";

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@ocr-monorepo/ui/components/carousel";
import { useEffect, useState } from "react";

export type BlogImageCarouselSlide = {
  src: string;
  darkSrc?: string;
  alt: string;
  title: string;
  caption: string;
};

type BlogImageCarouselProps = {
  slides: BlogImageCarouselSlide[];
  ariaLabel?: string;
  layout?: "image" | "document";
};

export function BlogImageCarousel({
  slides,
  ariaLabel = "Article screenshots",
  layout = "image",
}: BlogImageCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    const updateCurrent = () => setCurrent(api.selectedScrollSnap());
    updateCurrent();
    api.on("select", updateCurrent);
    api.on("reInit", updateCurrent);

    return () => {
      api.off("select", updateCurrent);
      api.off("reInit", updateCurrent);
    };
  }, [api]);

  if (slides.length === 0) return null;

  const activeSlide = slides[current] ?? slides[0];

  return (
    <figure
      className={
        layout === "document"
          ? "mx-auto my-6 max-w-lg space-y-2"
          : "my-8 space-y-3"
      }
    >
      <Carousel
        setApi={setApi}
        opts={{ align: "start" }}
        aria-label={ariaLabel}
        className="overflow-hidden rounded-md border border-border bg-background"
      >
        <CarouselContent className="ml-0">
          {slides.map((slide, index) => (
            <CarouselItem
              key={slide.src}
              className="pl-0"
              aria-label={`${index + 1} of ${slides.length}: ${slide.title}`}
            >
              <div
                className={
                  layout === "document"
                    ? "flex items-center justify-center bg-muted/30 p-3 sm:p-4"
                    : undefined
                }
              >
                {/* Article images are public content assets with author-supplied dimensions. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className={`${
                    layout === "document"
                      ? "h-auto max-h-[30rem] w-auto max-w-full object-contain shadow-sm"
                      : "h-auto w-full"
                  } ${slide.darkSrc ? "dark:hidden" : ""}`}
                />
                {slide.darkSrc ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={slide.darkSrc}
                    alt={slide.alt}
                    className={`hidden dark:block ${
                      layout === "document"
                        ? "h-auto max-h-[30rem] w-auto max-w-full object-contain shadow-sm"
                        : "h-auto w-full"
                    }`}
                  />
                ) : null}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-3 border-foreground bg-foreground text-background shadow-sm hover:bg-foreground/90 hover:text-background focus-visible:border-foreground focus-visible:ring-foreground/40 disabled:bg-foreground disabled:text-background dark:border-foreground dark:bg-foreground dark:text-background dark:hover:bg-foreground/90 dark:hover:text-background dark:focus-visible:border-foreground dark:focus-visible:ring-foreground/40 dark:disabled:bg-foreground dark:disabled:text-background" />
        <CarouselNext className="right-3 border-foreground bg-foreground text-background shadow-sm hover:bg-foreground/90 hover:text-background focus-visible:border-foreground focus-visible:ring-foreground/40 disabled:bg-foreground disabled:text-background dark:border-foreground dark:bg-foreground dark:text-background dark:hover:bg-foreground/90 dark:hover:text-background dark:focus-visible:border-foreground dark:focus-visible:ring-foreground/40 dark:disabled:bg-foreground dark:disabled:text-background" />
      </Carousel>

      <figcaption className="flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-start sm:justify-between">
        <span>
          <strong className="font-medium text-foreground">
            {activeSlide.title}:
          </strong>{" "}
          {activeSlide.caption}
        </span>
        <span className="flex shrink-0 gap-1.5" aria-label="Choose screenshot">
          {slides.map((slide, index) => (
            <button
              key={slide.src}
              type="button"
              onClick={() => api?.scrollTo(index)}
              className={`h-1.5 w-8 rounded-full transition-colors ${
                index === current ? "bg-primary" : "bg-border hover:bg-muted-foreground/50"
              }`}
              aria-label={`Show ${slide.title}`}
              aria-current={index === current ? "true" : undefined}
            />
          ))}
        </span>
      </figcaption>
    </figure>
  );
}
